import { TFunction } from 'react-i18next';
import {
  ChartOptions,
  ColorType,
  DeepPartial,
  HistogramData,
  LineData,
  LineStyle,
  TickMarkType,
  Time,
  UTCTimestamp,
  WhitespaceData
} from 'lightweight-charts';

import { OddsTypes } from 'constants/app';
import {
  BACKGROUND_DARK_THEME_COLOR,
  BACKGROUND_LIGHT_THEME_COLOR,
  BORDER_DARK_THEME_COLOR,
  BORDER_LIGHT_THEME_COLOR,
  ChartSelections,
  INTERVAL_1_DAY,
  INTERVAL_2_HOURS,
  INTERVAL_5_MIN,
  INTERVAL_15_SEC,
  MarketGraphChartDataGroupingPeriods,
  ONE_HOUR_IN_MILLISECONDS,
  TEXT_COLOR
} from 'constants/marketGraph';
import { MarketGraphChartSelection, MarketGraphPoint } from 'types/marketGraph';
import { getMonthLabelTranslationKey } from 'utils/date';

import convertOdds from './oddsConverter';
import { addZero } from './session';

/**
 * Calculates the weighted average of odds values based on volume
 *
 * @param data - Array of market graph points within a time interval
 * @param intervalStart - The timestamp marking the start of the interval
 * @returns A market graph point with weighted average odds and total volume
 */
const calculateWeightedAverage = (data: MarketGraphPoint[], intervalStart: number) => {
  if (data.length === 1) {
    return {
      timestamp: intervalStart as UTCTimestamp,
      value: data[0].value,
      volume: data[0].volume
    };
  }

  let totalVolume = 0;
  let weightedOddsSum = 0;

  for (const point of data) {
    const volume = point.volume ?? 0;
    const odds = point.value ?? 0;

    totalVolume += volume;
    weightedOddsSum += (odds - 1) * volume;
  }

  const avgOdds = totalVolume === 0 ? undefined : weightedOddsSum / totalVolume + 1;

  return {
    timestamp: intervalStart as UTCTimestamp,
    value: avgOdds,
    volume: totalVolume
  };
};

/**
 * Groups chart data points by specified time intervals
 *
 * This function aggregates data points that fall within the same time interval,
 * calculating weighted averages for odds values and summing volumes.
 * It also fills gaps in the data with the previous value to ensure continuous visualization.
 *
 * @param data - Array of market graph data points
 * @param interval - Time interval in milliseconds
 * @returns Array of normalized data points grouped by interval
 */
export const groupChartDataByInterval = ({ data, interval }: { data: MarketGraphPoint[]; interval: number }) => {
  const normalizedData: MarketGraphPoint[] = [];

  if (!data || data.length === 0 || interval <= 0) {
    return normalizedData;
  }

  let currentIntervalStart = Math.floor(data[0].timestamp / interval) * interval;
  let currentIntervalEnd = currentIntervalStart + interval;
  let dataIndex = 0; // Index to iterate through the sorted data
  let normalizedDataIndex = 0;

  while (currentIntervalEnd <= data[data.length - 1].timestamp + interval) {
    // Iterate until past the last data point + one interval to fill gaps
    const currentIntervalData = [];

    //Collect data points within the current interval
    while (
      dataIndex < data.length &&
      data[dataIndex].timestamp >= currentIntervalStart &&
      data[dataIndex].timestamp < currentIntervalEnd
    ) {
      currentIntervalData.push(data[dataIndex]);
      dataIndex++;
    }

    if (currentIntervalData.length > 0) {
      normalizedData.push(calculateWeightedAverage(currentIntervalData, currentIntervalStart));
    } else {
      const prevData = normalizedDataIndex > 0 ? normalizedData[normalizedDataIndex - 1] : undefined;

      normalizedData.push({
        timestamp: currentIntervalStart,
        value: prevData?.value
      });
    }

    currentIntervalStart = currentIntervalEnd;
    currentIntervalEnd += interval;
    normalizedDataIndex++;
  }

  return normalizedData;
};

/**
 * Formats time values for chart tick marks
 *
 * This function converts timestamp values to human-readable date/time formats
 * based on the tick mark type and user preferences.
 *
 * @param time - Time value to format
 * @param americanDateFormatEnabled - Whether to use American date format (MM/DD)
 * @param tickMarkType - Type of tick mark (year, month, day, hour, minute)
 * @param t - Translation function
 * @param locale - Locale string (reserved for future use)
 * @returns Formatted time string
 */
export const tickTimeFormatter = ({
  time,
  americanDateFormatEnabled,
  // locale, not used, but can be used in future, get it from lightweight-charts by default
  tickMarkType,
  t
}: {
  time: Time;
  tickMarkType: TickMarkType;
  locale: string;
  americanDateFormatEnabled: boolean;
  t: TFunction;
}) => {
  const date = new Date((time as number) * 1000);

  if (tickMarkType === 0) {
    return date.getFullYear();
  } else if (tickMarkType === 1) {
    return t(getMonthLabelTranslationKey(date.getMonth(), true));
  }
  if (tickMarkType === 2) {
    const day = date.getDate();
    const month = t(getMonthLabelTranslationKey(date.getMonth(), true));

    return `${americanDateFormatEnabled ? month : day} ${americanDateFormatEnabled ? day : month}`;
  } else if (tickMarkType === 3 || tickMarkType === 4) {
    const hours = date.getHours();
    const displayedHours = `${addZero(hours)}${hours}`;
    const minutes = date.getMinutes();
    const displayedMinutes = `${addZero(minutes)}${minutes}`;
    let displayedSeconds: string | undefined;

    if (tickMarkType === 4) {
      const seconds = date.getSeconds();
      displayedSeconds = `${addZero(seconds)}${seconds}`;
    }

    return `${displayedHours}:${displayedMinutes}${displayedSeconds ? `:${displayedSeconds}` : ''}`;
  }
};

/**
 * Converts odds value to chance percentage
 *
 * @param value - Odds value
 * @returns Chance percentage (1/odds * 100)
 */
export const convertOddsChance = (value: number) => {
  return (1 / value) * 100;
};

/**
 * Gets the appropriate value for line chart data based on selection
 *
 * If the selection is "odds", returns the original value.
 * If the selection is "chance", converts the odds to chance percentage.
 *
 * @param value - Odds value
 * @param selection - Chart selection type (odds or chance)
 * @returns Value for chart display
 */
export const getLineChartDataValue = ({
  value,
  selection
}: {
  value?: number;
  selection: MarketGraphChartSelection;
}) => {
  if (selection === ChartSelections.odds || !value) {
    return value;
  }

  return convertOddsChance(value);
};

/**
 * Converts millisecond timestamp to seconds for chart time values
 *
 * @param timestamp - Timestamp in milliseconds
 * @returns Timestamp in seconds as UTCTimestamp
 */
export const getChartTimeValue = (timestamp: number) => (timestamp / 1000) as UTCTimestamp;

/**
 * Determines the appropriate time interval and period for chart data
 * based on the time range
 *
 * - For ranges > 31 days: use 1-day intervals
 * - For ranges 1-31 days: use 2-hour intervals
 * - For ranges 1 hour to 1 day: use 5-minute intervals
 * - For ranges < 1 hour: use 15-second intervals
 *
 * @param startTimestamp - Start timestamp in milliseconds
 * @param endTimestamp - End timestamp in milliseconds
 * @returns Object with interval (in ms) and period type
 */
export const getChartInterval = ({
  startTimestamp,
  endTimestamp
}: {
  startTimestamp: number;
  endTimestamp: number;
}) => {
  const diff = endTimestamp - startTimestamp;

  if (diff / INTERVAL_1_DAY > 31) {
    return { interval: INTERVAL_1_DAY, period: MarketGraphChartDataGroupingPeriods.day1 };
  }

  if (diff / INTERVAL_1_DAY > 1 && diff / INTERVAL_1_DAY <= 31) {
    return { interval: INTERVAL_2_HOURS, period: MarketGraphChartDataGroupingPeriods.hour2 };
  }

  if (diff / ONE_HOUR_IN_MILLISECONDS > 1 && diff / INTERVAL_1_DAY <= 1) {
    return { interval: INTERVAL_5_MIN, period: MarketGraphChartDataGroupingPeriods.min5 };
  }

  return { interval: INTERVAL_15_SEC, period: MarketGraphChartDataGroupingPeriods.sec15 };
};

/**
 * Converts raw market graph data to line and histogram data for chart display
 *
 * This function:
 * 1. Groups the raw data by the specified interval
 * 2. Converts timestamps to chart time format
 * 3. Processes values based on the selected view (odds or chance)
 * 4. Creates separate arrays for line data (odds/chance) and histogram data (volume)
 *
 * @param chartData - Raw market graph data points
 * @param chartSelection - Chart selection type (odds or chance)
 * @param interval - Time interval in milliseconds
 * @returns Object with lineData and histogramData arrays
 */
export const getLineAndHistogramData = ({
  chartData,
  chartSelection,
  interval
}: {
  chartData: MarketGraphPoint[];
  chartSelection: MarketGraphChartSelection;
  interval: number;
}) => {
  const lineData: (WhitespaceData | LineData)[] = [];
  const histogramData: (WhitespaceData | HistogramData)[] = [];

  const groupedChartData = groupChartDataByInterval({ data: chartData, interval });

  for (const item of groupedChartData) {
    const time = getChartTimeValue(item.timestamp);
    const oddsValue = getLineChartDataValue({
      value: item.value,
      selection: chartSelection
    });

    lineData.push({
      time,
      value: oddsValue
    });
    histogramData.push({ time, value: item.volume });
  }

  return { lineData, histogramData };
};

/**
 * Creates configuration options for the market graph chart
 *
 * This function configures the appearance and behavior of the chart based on user preferences
 * and device characteristics. It handles:
 * - Theme colors (light/dark)
 * - Price scale visibility
 * - Grid appearance
 * - Crosshair behavior
 * - Time scale formatting
 *
 * @param isDarkTheme - Whether dark theme is enabled
 * @param americanDateFormatEnabled - Whether to use American date format
 * @param t - Translation function
 * @param width - Chart width
 * @param isSimplifiedView - Whether simplified view is enabled
 * @returns Chart options configuration object
 */
export const getChartOptions = ({
  isDarkTheme,
  americanDateFormatEnabled,
  t,
  width,
  isSimplifiedView
}: {
  isDarkTheme: boolean;
  width: number;
  isSimplifiedView: boolean;
  americanDateFormatEnabled: boolean;
  t: TFunction;
}): DeepPartial<ChartOptions> => {
  return {
    layout: {
      background: {
        type: ColorType.Solid,
        color: isDarkTheme ? BACKGROUND_DARK_THEME_COLOR : BACKGROUND_LIGHT_THEME_COLOR
      },
      textColor: TEXT_COLOR,
      fontSize: 12,
      attributionLogo: false,
      panes: {
        enableResize: false
      }
    },
    width,
    rightPriceScale: {
      visible: !isSimplifiedView, // Hide volume Y-scale if simplified view ia applied
      borderVisible: false,
      entireTextOnly: true
    },
    leftPriceScale: {
      visible: true,
      borderVisible: false,
      entireTextOnly: true
    },
    grid: {
      vertLines: { visible: false }, // Hide vertical grid lines
      horzLines: { color: isDarkTheme ? BORDER_DARK_THEME_COLOR : BORDER_LIGHT_THEME_COLOR, style: LineStyle.Dashed } // Dashed horizontal lines
    },
    crosshair: {
      // hide the horizontal crosshair label and line
      horzLine: {
        visible: false,
        labelVisible: false
      },
      // hide the vertical crosshair label and line
      vertLine: {
        visible: false,
        labelVisible: false
      }
    },
    handleScale: {
      axisPressedMouseMove: {
        price: false
      }
    },
    timeScale: {
      timeVisible: true,
      secondsVisible: true,
      fixRightEdge: true,
      fixLeftEdge: true,
      ticksVisible: true,
      borderColor: isDarkTheme ? BORDER_DARK_THEME_COLOR : BORDER_LIGHT_THEME_COLOR,
      tickMarkFormatter: (time: Time, tickMarkType: TickMarkType, locale: string) =>
        tickTimeFormatter({ time, tickMarkType, locale, americanDateFormatEnabled, t }),
      rightBarStaysOnScroll: false,
      allowShiftVisibleRangeOnWhitespaceReplacement: true,
      allowBoldLabels: false
    }
  };
};

/**
 * Converts line chart data from odds to chance format
 *
 * This function is used when switching between odds and chance views.
 * It transforms each data point's value using the convertOddsChance function.
 *
 * @param data - Array of line chart data points
 * @returns Array with values converted to chance percentages
 */
export const convertLineChartDataForSelection = ({ data }: { data: (WhitespaceData | LineData)[] }) => {
  return data.map(item => {
    if ('value' in item) {
      return {
        ...item,
        value: convertOddsChance(item.value)
      };
    }

    return item;
  });
};

/**
 * Combines existing and new chart data, handling overlapping timestamps
 *
 * This function is used when updating the chart with new data points.
 * It handles the special case where the last point of existing data and
 * the first point of new data have the same timestamp, combining them
 * into a single weighted average point.
 *
 * @param prevLineChartData - Existing line chart data
 * @param newLineChartData - New line chart data to add
 * @param prevHistogramChartData - Existing histogram chart data
 * @param newHistogramChartData - New histogram chart data to add
 * @returns Combined line and histogram data arrays
 */
export const concatExistAndPrevChartData = ({
  prevLineChartData,
  newLineChartData,
  prevHistogramChartData,
  newHistogramChartData
}: {
  prevLineChartData: (WhitespaceData | LineData)[];
  newLineChartData: (WhitespaceData | LineData)[];
  prevHistogramChartData: (WhitespaceData | HistogramData)[];
  newHistogramChartData: (WhitespaceData | HistogramData)[];
}) => {
  if (!prevLineChartData.length) {
    return { lineData: newLineChartData, histogramData: newHistogramChartData };
  }

  if (!newLineChartData.length) {
    return { lineData: prevLineChartData, histogramData: prevHistogramChartData };
  }

  let lineDataResult: (WhitespaceData | LineData)[] = [];
  let histogramDataResult: (WhitespaceData | HistogramData)[] = [];

  const lastPointFromPrevLineData = prevLineChartData[prevLineChartData.length - 1];
  const [firstPointFromNewLineData, ...restNewLineData] = newLineChartData;

  /**
   * Timestamps always should be unique in data array for LineSeries and HistogramSeries, so if timestamp from last the
   * point of grouped data is equal to timestamp from the first point of new grouped data need to combine them into 1 point
   */
  if (lastPointFromPrevLineData.time === firstPointFromNewLineData.time) {
    const lastPointFromPrevHistogramData = prevHistogramChartData[prevHistogramChartData.length - 1];
    const [firstPointFromNewHistogramData, ...restNewHistogramData] = newHistogramChartData;

    if (
      'value' in firstPointFromNewLineData &&
      'value' in lastPointFromPrevLineData &&
      'value' in firstPointFromNewHistogramData &&
      'value' in lastPointFromPrevHistogramData
    ) {
      const totalVolume = lastPointFromPrevHistogramData.value + firstPointFromNewHistogramData.value;
      const weightedOddsSum =
        lastPointFromPrevLineData.value + (firstPointFromNewLineData.value - 1) * firstPointFromNewHistogramData.value;
      const avgOdds = totalVolume === 0 ? undefined : weightedOddsSum / totalVolume + 1;

      lineDataResult = [
        ...prevLineChartData.slice(0, -1),
        {
          ...lastPointFromPrevHistogramData,
          value: avgOdds
        },
        ...restNewLineData
      ];

      histogramDataResult = [
        ...prevHistogramChartData.slice(0, -1),
        {
          ...lastPointFromPrevHistogramData,
          value: totalVolume
        },
        ...restNewHistogramData
      ];
    }
  } else {
    lineDataResult = prevLineChartData.concat(newLineChartData);
    histogramDataResult = prevHistogramChartData.concat(newHistogramChartData);
  }

  return { lineData: lineDataResult, histogramData: histogramDataResult };
};

/**
 * Gets the appropriate odds value based on odds type preference
 *
 * Converts odds to American format if American odds are enabled.
 *
 * @param odds - Odds value
 * @param isAmericanOdds - Whether American odds format is enabled
 * @returns Formatted odds value
 */
export const getOddsValue = ({ odds, isAmericanOdds }: { odds: number | string; isAmericanOdds: boolean }) => {
  if (isAmericanOdds) {
    return convertOdds(odds, OddsTypes.US);
  }

  return odds;
};
